import { ASH_ADMIN, ORGANIZATION_ADMIN, PROGRAM_ADMIN } from '@/constants/clientPortalRoles'
import {
  findIdentityScopeByNormalizedPartnerId,
  getRolesFromScope,
  hasNonPhiRole,
  narrowIdentityScope,
  rolesInclude,
  type IdentityScopes,
} from '@/utilities/auth/identityScopeRoles'

const GLOBAL_SCOPE_KEY = '*'

const CLIENT_FILES_ALLOWED_ROLES = [ASH_ADMIN, ORGANIZATION_ADMIN, PROGRAM_ADMIN] as const

/**
 * Matches portal-api AuthUserIdentitySchema.is_client_super_admin for Files role checks.
 */
export function isClientSuperAdmin(scopes: IdentityScopes | null | undefined): boolean {
  if (!scopes || typeof scopes !== 'object') return false

  for (const scope of Object.values(scopes)) {
    const roles = getRolesFromScope(narrowIdentityScope(scope))
    if (rolesInclude(roles, [ASH_ADMIN])) return true
  }

  const globalRoles = getRolesFromScope(narrowIdentityScope(scopes[GLOBAL_SCOPE_KEY]))
  return rolesInclude(globalRoles, [ORGANIZATION_ADMIN, PROGRAM_ADMIN])
}

/**
 * Matches portal-api IdentityRoleGuard on client files routes (allowed_roles + super-admin bypass).
 */
export function hasClientPortalFilesRole(
  scopes: IdentityScopes | null | undefined,
  partnerId: string | null | undefined,
): boolean {
  if (!scopes) return false
  if (isClientSuperAdmin(scopes)) return true

  const partnerRoles = getRolesFromScope(
    findIdentityScopeByNormalizedPartnerId(scopes, partnerId) ?? {},
  )
  return rolesInclude(partnerRoles, CLIENT_FILES_ALLOWED_ROLES)
}

function getActivePartnerRoles(
  scopes: IdentityScopes,
  partnerId: string | null | undefined,
): string[] {
  return getRolesFromScope(findIdentityScopeByNormalizedPartnerId(scopes, partnerId) ?? {})
}

/**
 * Files access: portal-api role rules plus deny when the active partner has a non-PHI role.
 */
export function canAccessClientPortalFiles(
  scopes: IdentityScopes | null | undefined,
  partnerId: string | null | undefined,
): boolean {
  if (!scopes || !hasClientPortalFilesRole(scopes, partnerId)) return false
  return !hasNonPhiRole(getActivePartnerRoles(scopes, partnerId))
}
